/**
 * WebPushPermissionModal — the one-time "Allow notifications?" prompt.
 *
 * Shown after the user's first meaningful action (first patient created /
 * first appointment booked), never on first page load. The localStorage gate
 * below ensures we ask at most once per browser regardless of how the trigger
 * fires; Step 5's trigger reads `hasAskedWebPushPermission()` before opening.
 *
 * The modal drives the subscribe flow via useWebPushNotification; it does not
 * decide *when* to appear — that's the trigger's job.
 */

import React from "react";
import { toast } from "react-toastify";

import { useWebPushNotification } from "../../hooks/useWebPushNotification";

const PERMISSION_ASKED_KEY = "kaagazy:webpush-permission-asked";

export function hasAskedWebPushPermission(): boolean {
  try {
    return localStorage.getItem(PERMISSION_ASKED_KEY) === "true";
  } catch {
    // localStorage blocked (private mode / hardened browser) — safe to ask.
    return false;
  }
}

export function markWebPushPermissionAsked(): void {
  try {
    localStorage.setItem(PERMISSION_ASKED_KEY, "true");
  } catch {
    /* non-fatal */
  }
}

/**
 * Should the trigger open the prompt right now? True only when:
 *  - we haven't asked before,
 *  - we're online (no point prompting a subscribe that needs the network),
 *  - the browser hasn't already granted/denied (permission === "default"),
 *  - and Web Push is actually supported here.
 */
export function shouldPromptWebPush(): boolean {
  if (hasAskedWebPushPermission()) return false;
  if (typeof navigator === "undefined" || !navigator.onLine) return false;
  if (typeof Notification === "undefined" || Notification.permission !== "default") return false;
  if (!("serviceWorker" in navigator)) return false;
  if (typeof window === "undefined" || !("PushManager" in window)) return false;
  return true;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const WebPushPermissionModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { permission, isSubscribing, subscribe } = useWebPushNotification();

  if (!isOpen) return null;

  // Asked once, regardless of the answer — close always records it.
  const close = () => {
    markWebPushPermissionAsked();
    onClose();
  };

  const handleAllow = async () => {
    const ok = await subscribe();
    markWebPushPermissionAsked();
    if (ok) {
      toast.success("Notifications enabled");
    } else if (typeof Notification !== "undefined" && Notification.permission === "denied") {
      // Read the LIVE browser value, not the hook's React state — the latter
      // hasn't re-rendered yet inside this closure.
      toast.info("Notifications are blocked in your browser settings");
    } else {
      toast.info("Notifications were not enabled");
    }
    onClose();
  };

  const blocked = permission === "denied";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">Stay updated</h2>
          <button
            onClick={close}
            className="text-slate-400 hover:text-slate-600 text-xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {blocked ? (
            <>
              <p className="text-sm text-slate-500">
                Notifications are blocked for this site. To turn them on, allow
                notifications in your browser&apos;s site settings, then reload
                and try again.
              </p>
              <button
                onClick={close}
                className="w-full bg-brand text-white py-2.5 rounded-xl text-sm font-medium hover:bg-brand-dark"
              >
                Got it
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-500">
                Get notified about appointments and prescriptions even when this
                tab is closed or the app is in the background.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={close}
                  disabled={isSubscribing}
                  className="flex-1 border border-slate-200 text-slate-600 py-2.5 rounded-xl text-sm hover:bg-slate-50 disabled:opacity-40"
                >
                  Not now
                </button>
                <button
                  onClick={handleAllow}
                  disabled={isSubscribing}
                  className="flex-1 bg-brand text-white py-2.5 rounded-xl text-sm font-medium hover:bg-brand-dark disabled:opacity-40"
                >
                  {isSubscribing ? "Enabling…" : "Allow notifications"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
